import React from 'react';
import { ChevronLeft, Plus, MapPin } from 'lucide-react';
import { useCustomerContacts } from '../../lib/hooks';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface AddressManagerProps {
  customerId: string;
  onBack: () => void;
}

const AddressManager: React.FC<AddressManagerProps> = ({ customerId, onBack }) => {
  const { contacts, addContact, updateContact, deleteContact } = useCustomerContacts(customerId);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [addressData, setAddressData] = React.useState<{
    address: string;
    city: string;
    state: string;
    zip_code: string;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const addresses = contacts.filter(contact => contact.type === 'address');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!addressData) {
      setError('Please select a valid address from the suggestions');
      return;
    }

    try {
      const form = e.target as HTMLFormElement;
      await addContact('address', {
        ...addressData,
        is_primary: form.is_primary.checked,
        label: form.label.value
      });
      setShowAddForm(false);
      setAddressData(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add address');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center space-x-4 mb-8">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Manage Addresses</h1>
          <p className="text-muted-foreground">Add and manage customer addresses</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 text-sm text-destructive bg-destructive/10 rounded-lg">
          {error}
        </div>
      )}

      {/* Add Address Form */}
      {showAddForm ? (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Add New Address</CardTitle>
            <CardDescription>Enter the address details below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>Address</Label>
                  <AddressAutocomplete
                    onAddressSelect={setAddressData}
                    className="w-full"
                  />
                </div>

                <div>
                  <Label>Label (Optional)</Label>
                  <Input
                    name="label"
                    placeholder="e.g., Home, Office, etc."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="is_primary" name="is_primary" />
                  <Label htmlFor="is_primary">Set as primary address</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setAddressData(null);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Add Address
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowAddForm(true)} className="mb-8">
          <Plus className="w-4 h-4 mr-2" />
          Add Address
        </Button>
      )}

      {/* Address List */}
      <div className="space-y-4">
        {addresses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No addresses yet</h3>
              <p className="text-muted-foreground mt-1">Add an address to get started</p>
            </CardContent>
          </Card>
        ) : (
          addresses.map((address) => (
            <Card key={address.id} className="group">
              <CardContent className="flex items-center justify-between p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-primary/10 text-primary rounded-lg">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">{address.value}</p>
                    {address.label && (
                      <p className="text-sm text-muted-foreground mt-1">{address.label}</p>
                    )}
                    {address.isPrimary && (
                      <div className="mt-2">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                          Primary Address
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!address.isPrimary) {
                        updateContact({
                          ...address,
                          isPrimary: true,
                          raw: { ...address.raw, is_primary: true }
                        });
                      }
                    }}
                    disabled={address.isPrimary}
                  >
                    Set as Primary
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this address?')) {
                        deleteContact(address);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AddressManager;